import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, apiErrorMessage } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, PencilSimple, Trash, Buildings, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

function emptyForm() {
  return { name: "", pnl_url: "", balance_sheet_url: "", budget_url: "" };
}

export default function BusinessesPage() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "secondary_admin";
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/businesses");
      setBusinesses(data);
    } catch (e) {
      { const _m = apiErrorMessage(e); if (_m) toast.error(_m); }
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setOpen(true);
  };

  const startEdit = (b) => {
    setEditingId(b.id);
    setForm({
      name: b.name || "",
      pnl_url: b.pnl_url || "",
      balance_sheet_url: b.balance_sheet_url || "",
      budget_url: b.budget_url || "",
    });
    setOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Business name is required");
      return;
    }
    setSubmitting(true);
    try {
      if (editingId) {
        await api.put(`/businesses/${editingId}`, form);
        toast.success("Business updated");
      } else {
        await api.post("/businesses", form);
        toast.success("Business added");
      }
      setOpen(false);
      await load();
    } catch (e) {
      { const _m = apiErrorMessage(e); if (_m) toast.error(_m); }
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/businesses/${id}`);
      toast.success("Business deleted");
      await load();
    } catch (e) {
      { const _m = apiErrorMessage(e); if (_m) toast.error(_m); }
    }
  };

  return (
    <div className="min-h-screen app-bg">
      <AppHeader />

      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to="/dashboard" className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1 mb-2">
              <ArrowLeft size={14} /> Back to dashboard
            </Link>
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Businesses</h1>
            <p className="mt-1 text-sm text-slate-500">
              {canEdit
                ? "Each business connects to its own Google Sheets exported from QuickBooks."
                : "Businesses you have view access to. Contact an admin to request additional access."}
            </p>
          </div>

          {canEdit ? (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button onClick={startCreate} className="bg-[#327de3] hover:bg-[#1f64c2] text-white" data-testid="open-add-business">
                  <Plus size={16} className="mr-1.5" /> Add business
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg" data-testid="business-dialog">
              <DialogHeader>
                <DialogTitle className="font-display">{editingId ? "Edit business" : "Add a business"}</DialogTitle>
                <DialogDescription>
                  Provide a name and at least one public Google Sheet URL. Make sure the sheet is shared as "Anyone with the link".
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={onSubmit} className="space-y-4 mt-2">
                <div className="space-y-1.5">
                  <Label htmlFor="biz-name" className="text-xs font-semibold uppercase tracking-widest text-slate-500">Business name</Label>
                  <Input id="biz-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Acme Inc." data-testid="biz-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="budget-url" className="text-xs font-semibold uppercase tracking-widest text-slate-500">Project Summary sheet URL (SummaryA tab)</Label>
                  <Input id="budget-url" value={form.budget_url} onChange={(e) => setForm({ ...form, budget_url: e.target.value })} placeholder="https://docs.google.com/spreadsheets/d/..." data-testid="biz-budget-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pnl-url" className="text-xs font-semibold uppercase tracking-widest text-slate-500">Profit & Loss sheet URL</Label>
                  <Input id="pnl-url" value={form.pnl_url} onChange={(e) => setForm({ ...form, pnl_url: e.target.value })} placeholder="https://docs.google.com/spreadsheets/d/..." data-testid="biz-pnl-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="bs-url" className="text-xs font-semibold uppercase tracking-widest text-slate-500">Balance Sheet URL</Label>
                  <Input id="bs-url" value={form.balance_sheet_url} onChange={(e) => setForm({ ...form, balance_sheet_url: e.target.value })} placeholder="https://docs.google.com/spreadsheets/d/..." data-testid="biz-bs-input" />
                </div>

                <DialogFooter className="mt-4">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={submitting} className="bg-[#327de3] hover:bg-[#1f64c2] text-white" data-testid="biz-submit-button">
                    {submitting ? "Saving…" : editingId ? "Save changes" : "Add business"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          ) : null}
        </div>

        {loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : businesses.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <div className="w-14 h-14 rounded-full bg-[#327de3]/10 flex items-center justify-center mx-auto">
              <Buildings size={26} weight="bold" className="text-[#327de3]" />
            </div>
            <h2 className="mt-4 font-display text-xl font-bold text-slate-900">{canEdit ? "No businesses yet" : "No businesses to view"}</h2>
            <p className="mt-2 text-sm text-slate-500">
              {canEdit
                ? "Add your first business to start seeing data."
                : "An admin hasn't granted you access to any business yet. Reach out to your admin to request access."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {businesses.map((b) => (
              <div key={b.id} className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden" data-testid={`business-card-${b.id}`}>
                <div className="h-1 bg-[#327de3]" />
                <div className="p-6">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="font-display text-lg font-semibold text-slate-900 truncate">{b.name}</h3>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {b.last_refreshed ? `Updated ${new Date(b.last_refreshed).toLocaleString()}` : "Not refreshed yet"}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      {canEdit && (
                        <>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => startEdit(b)} data-testid={`edit-${b.id}`}>
                            <PencilSimple size={16} />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-[#D92D20] hover:text-[#D92D20] hover:bg-red-50" data-testid={`delete-${b.id}`}>
                                <Trash size={16} />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete this business?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently remove <strong>{b.name}</strong> and its cached reports. This cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => remove(b.id)} className="bg-[#D92D20] hover:bg-red-700">Delete</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="mt-6 grid grid-cols-3 gap-2">
                    <BizActionButton
                      label="Project Summary"
                      to={b.budget_url ? `/businesses/${b.id}/project-summary` : null}
                      testId={`btn-summary-${b.id}`}
                    />
                    <BizActionButton
                      label="PNL"
                      to={b.pnl_url ? `/businesses/${b.id}/project-summary?view=pnl` : null}
                      testId={`btn-pnl-${b.id}`}
                    />
                    <BizActionButton
                      label="Balance Sheet"
                      to={b.balance_sheet_url ? `/businesses/${b.id}/project-summary?view=balance` : null}
                      testId={`btn-balance-${b.id}`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function BizActionButton({ label, to, testId }) {
  const base = "w-full h-10 rounded-md text-[11px] font-bold uppercase tracking-widest flex items-center justify-center text-center px-1 transition-colors";
  if (!to) {
    return (
      <div
        className={`${base} bg-slate-100 text-slate-400 border border-dashed border-slate-200 cursor-not-allowed`}
        data-testid={`${testId}-disabled`}
        title="No sheet connected"
      >
        {label}
      </div>
    );
  }
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`${base} bg-[#327de3] hover:bg-[#1f64c2] text-white shadow-sm`}
    >
      {label}
    </Link>
  );
}
